import { MODULE_TYPES, NEED_KINDS, createRng, type ModuleType, type NeedKind, type ShipClass } from "@station-keeping/sim";
import { TILE_H, TILE_W } from "./iso";
import { COLORS, moduleColor, needColor } from "./palette";
import { NEED_ICONS } from "../pixel/icons";
import { PixelCanvas, mix, shade, type BoxFaces, type Color, type CylinderColors } from "./pixel";

/**
 * Les sprites du jeu, dessinés en code en pixel art avec la palette de l'interface. Rien n'est chargé :
 * tout naît ici, au démarrage, puis devient des textures. Vue isométrique 3:2, pixels fins (case de 72 × 48).
 */

/** Contour sombre des pièces (lisibilité façon arcade). */
const INK = 0x04060d;

/** Cadre d'un sprite de module : 72 × 120, losange de sol de 60 × 40 en bas. */
export const MODULE_W = 72;
export const MODULE_H = 120;
const FOOT_LEFT = 6;
const FOOT_W = 60;
const FOOT_H = 40;
const GROUND_TOP = 78;
const CX = MODULE_W / 2;
/** Les ellipses s'aplatissent comme les losanges de la vue. */
const SQUASH = FOOT_H / FOOT_W;
/** Point d'ancrage : le centre du losange de sol. */
export const MODULE_ANCHOR = { x: CX, y: GROUND_TOP + FOOT_H / 2 };

/** Coque des modules : un bleu nuit métallique en plusieurs tons, plus clair que le pont. */
const HULL = {
  top: 0x3a5088,
  plate: 0x4560a0,
  left: 0x2a3c6c,
  leftDark: 0x223258,
  right: 0x1c2a52,
  rightDark: 0x152140,
  rim: 0x6c8bd0,
  seam: 0x16223f,
  rivet: 0x8aa3dc,
};
type HullColors = typeof HULL;

export interface ModuleArt {
  body: PixelCanvas;
  /** Fenêtres et balises, allumées la nuit (pixels seuls, mêmes coordonnées). */
  lights: PixelCanvas | null;
}

function accentOf(type: ModuleType): { base: Color; light: Color; dark: Color; deep: Color } {
  const base = moduleColor(type);
  return { base, light: shade(base, 0.4), dark: shade(base, -0.4), deep: shade(base, -0.65) };
}

function tones(base: Color): CylinderColors {
  return { top: shade(base, 0.3), light: shade(base, 0.5), side: base, dark: shade(base, -0.4) };
}

/** Haut de la face latérale à la colonne `i` (0 à FOOT_W − 1) d'un bloc dont le toit commence à `roofTop`. */
function faceTop(i: number, roofTop: number): number {
  return roofTop + PixelCanvas.diamondBottomRow(FOOT_W, FOOT_H, i) + 1;
}

/**
 * Bande sur les faces d'un bloc : colonnes [i0, i1), `h` lignes à partir de `from` sous le haut de la face.
 * Elle suit la pente, comme une rangée de hublots ou un joint de tôle.
 */
function faceBand(
  art: PixelCanvas,
  roofTop: number,
  i0: number,
  i1: number,
  from: number,
  h: number,
  color: Color | ((i: number, row: number) => Color | null),
): void {
  for (let i = i0; i < i1; i++) {
    const top = faceTop(i, roofTop) + from;
    for (let row = 0; row < h; row++) {
      const c = typeof color === "function" ? color(i, row) : color;
      if (c !== null) art.set(FOOT_LEFT + i, top + row, c);
    }
  }
}

/** Hublot sur une face : cadre clair, vitre sombre, reflet ; sa vitre s'allume la nuit. */
function faceWindow(art: PixelCanvas, lights: PixelCanvas, roofTop: number, i: number, from: number, w: number, h: number, lit: Color): void {
  faceBand(art, roofTop, i - 1, i + w + 1, from - 1, h + 2, 0x5a72ad);
  faceBand(art, roofTop, i, i + w, from, h, 0x0b1430);
  faceBand(art, roofTop, i, i + 1, from, 1, 0x6f86c4);
  faceBand(lights, roofTop, i, i + w, from, h, lit);
}

/** Anneau autour d'un cylindre, à `k` pixels au-dessus de son bord avant. */
function cylinderBand(art: PixelCanvas, cx: number, baseY: number, rx: number, k: number, thickness: number, color: Color): void {
  const ry = Math.max(1, Math.round(rx * SQUASH));
  for (let dx = -rx; dx < rx; dx++) {
    const half = Math.round(ry * Math.sqrt(Math.max(0, 1 - ((dx + 0.5) / rx) ** 2)));
    for (let t = 0; t < thickness; t++) art.set(cx + dx, baseY + half - k + t, color);
  }
}

/** Quadrillage d'un losange, parallèle à ses arêtes (cellules solaires, plaques). */
function diamondGrid(art: PixelCanvas, left: number, top: number, w: number, h: number, divisions: number, color: Color): void {
  const L: [number, number] = [left, top + h / 2];
  const T: [number, number] = [left + w / 2, top];
  const R: [number, number] = [left + w - 1, top + h / 2];
  const B: [number, number] = [left + w / 2, top + h - 1];
  const at = (a: [number, number], b: [number, number], t: number): [number, number] => [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
  ];
  for (let k = 1; k < divisions; k++) {
    const t = k / divisions;
    const [ax, ay] = at(L, B, t);
    const [bx, by] = at(T, R, t);
    art.line(ax, ay, bx, by, color);
    const [cx, cy] = at(L, T, t);
    const [dx, dy] = at(B, R, t);
    art.line(cx, cy, dx, dy, color);
  }
}

/**
 * Coque d'un module : ombre portée sur le pont, bloc à quatre tons, plaque de toit, joint de tôle, arête
 * avant éclairée, rivets. Renvoie la ligne du haut du toit.
 */
function hull(art: PixelCanvas, height: number, overrides: Partial<HullColors> = {}): number {
  const c = { ...HULL, ...overrides };
  art.diamond(FOOT_LEFT + 5, GROUND_TOP + 4, FOOT_W, FOOT_H, 0x000000, undefined, 96);
  const roofTop = art.isoBox(FOOT_LEFT, GROUND_TOP, FOOT_W, FOOT_H, height, { top: c.top, left: c.left, right: c.right, rim: c.rim });
  // Pied des faces plus sombre, joint de tôle à mi-hauteur.
  faceBand(art, roofTop, 0, FOOT_W, Math.max(1, height - 3), Math.min(3, height - 1), (i) => (i < FOOT_W / 2 ? c.leftDark : c.rightDark));
  if (height >= 12) faceBand(art, roofTop, 0, FOOT_W, Math.floor(height / 2) - 1, 1, c.seam);
  // Arête avant : la lumière accroche le coin.
  const edge = FOOT_W / 2 - 1;
  for (let y = faceTop(edge, roofTop); y < faceTop(edge, roofTop) + height - 1; y++) art.set(FOOT_LEFT + edge, y, c.rim);
  // Rivets le long du haut des faces.
  for (let i = 3; i < FOOT_W; i += 7) {
    if (Math.abs(i - FOOT_W / 2) < 3) continue;
    art.set(FOOT_LEFT + i, faceTop(i, roofTop) + 2, c.rivet);
    art.set(FOOT_LEFT + i, faceTop(i, roofTop) + 3, c.seam);
  }
  // Plaque de toit, un ton plus claire, et son liseré.
  art.diamond(FOOT_LEFT + 8, roofTop + 5, FOOT_W - 16, FOOT_H - 10, c.plate);
  art.diamondEdge(FOOT_LEFT + 8, roofTop + 5, FOOT_W - 16, FOOT_H - 10, mix(c.plate, c.top, 0.5));
  return roofTop;
}

function core(art: PixelCanvas, lights: PixelCanvas): void {
  const roofTop = hull(art, 14, { rim: 0x9fbaf0 });
  const cy = roofTop + FOOT_H / 2;
  art.cylinder(CX, cy, 17, 16, { top: 0xf4f8ff, light: 0xffffff, side: 0xc9d5f0, dark: 0x8796be }, SQUASH);
  // Anneaux de confinement, cyan.
  for (const k of [5, 12]) {
    cylinderBand(art, CX, cy, 17, k, 2, COLORS.accent);
    cylinderBand(lights, CX, cy, 17, k, 2, 0xbff0ff);
  }
  // Cœur du réacteur, visible sur le dessus.
  const capY = cy - 16;
  art.ellipse(CX, capY, 11, 7, 0x2d6a9a);
  art.ellipse(CX, capY, 8, 5, 0x6fd3ff);
  art.ellipse(CX, capY, 4, 2, 0xdff8ff);
  lights.ellipse(CX, capY, 8, 5, 0x9fe6ff);
  lights.ellipse(CX, capY, 4, 2, 0xffffff);
  // Antenne, petite parabole, balise.
  art.rect(CX - 1, capY - 20, 2, 18, 0xc9d5f0);
  art.vline(CX, capY - 20, capY - 3, 0x8796be);
  art.ellipse(CX + 6, capY - 15, 5, 2, 0xdfe7ff);
  art.hline(CX + 1, CX + 5, capY - 15, 0x8796be);
  art.rect(CX - 1, capY - 23, 2, 2, 0xffffff);
  lights.rect(CX - 2, capY - 24, 4, 4, 0x9fe6ff);
  lights.rect(CX - 1, capY - 23, 2, 2, 0xffffff);
}

function dock(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("dock");
  const roofTop = hull(art, 6);
  // Aire d'appontage, bordée de cyan, avec son cercle de guidage.
  const left = FOOT_LEFT + 6;
  const top = roofTop + 4;
  art.diamond(left, top, 48, 32, 0x142246, accent.base);
  art.diamondEdge(left + 12, top + 8, 24, 16, accent.dark);
  // Chevrons d'approche vers l'extérieur.
  const cy = top + 16;
  for (const [dx, sign] of [
    [-10, -1],
    [10, 1],
  ] as const) {
    for (let k = 0; k < 3; k++) {
      art.set(CX + dx + sign * k, cy - 2 + k, accent.light);
      art.set(CX + dx + sign * k, cy + 2 - k, accent.light);
    }
  }
  art.rect(CX - 1, cy - 1, 2, 2, accent.light);
  // Feux aux quatre pointes de l'aire.
  const corners = PixelCanvas.diamondCorners(left, top, 48, 32);
  for (const [x, y] of [corners.top, corners.right, corners.bottom, corners.left]) {
    art.rect(x - 1, y - 1, 2, 2, accent.light);
    lights.rect(x - 2, y - 2, 4, 4, 0x6fd3ff);
    lights.rect(x - 1, y - 1, 2, 2, 0xe6fbff);
  }
  // Bras d'amarrage aux pointes gauche et droite du toit.
  for (const x of [FOOT_LEFT + 1, FOOT_LEFT + FOOT_W - 4]) {
    art.rect(x, roofTop + 10, 3, 11, accent.dark);
    art.vline(x, roofTop + 10, roofTop + 20, accent.base);
    art.rect(x, roofTop + 8, 3, 2, accent.light);
    lights.rect(x, roofTop + 8, 3, 2, 0xe6fbff);
  }
}

function solarPanel(art: PixelCanvas): void {
  const accent = accentOf("solar_panel");
  const roofTop = hull(art, 6, { top: 0x2f4374, plate: 0x34497c });
  // Mât central.
  art.rect(CX - 2, roofTop - 8, 2, 28, 0xa9b6d8);
  art.rect(CX, roofTop - 8, 2, 28, 0x6c7aa3);
  // Panneau incliné vers le soleil : épaisseur, cellules, cadre jaune, quadrillage, reflets.
  const pw = 54;
  const ph = 36;
  const left = CX - pw / 2;
  const top = roofTop - 30;
  art.diamond(left, top + 4, pw, ph, 0x0c1a3a);
  art.diamond(left, top, pw, ph, 0x1f3d84, accent.base);
  art.diamondEdge(left + 2, top + 1, pw - 4, ph - 2, accent.dark);
  diamondGrid(art, left + 2, top + 1, pw - 4, ph - 2, 4, 0x3a6ad0);
  for (const [x, y] of [
    [CX - 8, top + 8],
    [CX - 7, top + 8],
    [CX - 8, top + 9],
    [CX + 6, top + 16],
  ] as const) {
    art.set(x, y, 0xdfe9ff);
  }
}

function battery(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("battery");
  const roofTop = hull(art, 18);
  // Quatre cellules sur la face droite : boîtier, niveau de charge, borne.
  for (const i of [33, 39, 45, 51]) {
    faceBand(art, roofTop, i, i + 4, 3, 12, accent.deep);
    faceBand(art, roofTop, i + 1, i + 3, 4, 2, 0x2a2410);
    faceBand(art, roofTop, i + 1, i + 3, 6, 8, accent.base);
    faceBand(art, roofTop, i + 1, i + 2, 6, 8, accent.light);
  }
  // Grille d'aération et témoin de charge sur la face gauche.
  for (const row of [4, 7, 10]) faceBand(art, roofTop, 5, 21, row, 1, 0x141f3c);
  faceBand(art, roofTop, 24, 27, 4, 2, 0x5ee0a4);
  faceBand(lights, roofTop, 24, 27, 4, 2, 0xb8ffd9);
  // Bornes sur le toit : plus et moins.
  const cy = roofTop + FOOT_H / 2;
  art.cylinder(CX - 12, cy - 1, 5, 6, tones(accent.base), SQUASH);
  art.cylinder(CX + 12, cy + 3, 5, 6, tones(0xff7a8a), SQUASH);
  art.hline(CX - 14, CX - 10, cy - 7, accent.deep);
  art.vline(CX - 12, cy - 9, cy - 5, accent.deep);
  art.hline(CX + 10, CX + 14, cy - 3, 0x6a2030);
}

function o2Recycler(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("o2_recycler");
  const roofTop = hull(art, 10);
  const cy = roofTop + FOOT_H / 2;
  // Tuyau du réservoir vers le bord du toit.
  art.rect(FOOT_LEFT + 8, roofTop + 15, 12, 3, 0x8796be);
  art.hline(FOOT_LEFT + 8, FOOT_LEFT + 19, roofTop + 15, 0xc9d5f0);
  art.cylinder(CX, cy, 16, 22, tones(accent.base), SQUASH);
  cylinderBand(art, CX, cy, 16, 6, 2, accent.dark);
  cylinderBand(art, CX, cy, 16, 17, 2, accent.dark);
  // Voyant de niveau : une fente où montent des bulles.
  art.rect(CX - 3, cy - 14, 6, 8, 0x0b2a2a);
  for (const [x, y] of [
    [CX - 2, cy - 8],
    [CX + 1, cy - 10],
    [CX - 1, cy - 12],
    [CX + 1, cy - 13],
  ] as const) {
    art.set(x, y, 0xeafffa);
    lights.set(x, y, 0xb8fff0);
  }
  // Bouchon sur le dessus.
  art.ellipse(CX, cy - 22, 5, 3, accent.dark);
  art.ellipse(CX, cy - 23, 3, 2, accent.light);
}

function habitat(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("habitat");
  const roofTop = hull(art, 14);
  const cy = roofTop + FOOT_H / 2 + 2;
  art.ellipse(CX, cy, 24, 15, accent.deep);
  art.dome(CX, cy, 24, 22, accent.base, 0xffffff, accent.dark);
  // Reflet élargi et bandeau de hublots autour du dôme.
  art.rect(CX - 13, cy - 16, 3, 1, accent.light);
  art.rect(CX - 14, cy - 15, 2, 3, accent.light);
  for (let dx = -15; dx <= 15; dx += 6) {
    const y = cy - 8 + Math.round(Math.abs(dx) * 0.12);
    art.rect(CX + dx - 1, y, 3, 2, 0x1b1140);
    lights.rect(CX + dx - 1, y, 3, 2, 0xffe3a3);
  }
  // Fenêtres des quartiers, sur deux rangées.
  for (const i of [7, 16]) for (const from of [3, 8]) faceWindow(art, lights, roofTop, i, from, 4, 2, 0xffd98a);
  for (const i of [37, 46]) for (const from of [3, 8]) faceWindow(art, lights, roofTop, i, from, 4, 2, 0xffd98a);
}

function cantina(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("cantina");
  const roofTop = hull(art, 16);
  // Auvent rayé sur la face droite.
  faceBand(art, roofTop, 31, 59, 1, 3, (i) => (Math.floor(i / 3) % 2 === 0 ? accent.base : 0xfff0f6));
  // Grandes baies vitrées.
  for (const i of [6, 15]) faceWindow(art, lights, roofTop, i, 5, 6, 4, 0xffc2e0);
  for (const i of [36, 46]) faceWindow(art, lights, roofTop, i, 6, 6, 4, 0xffc2e0);
  // Enseigne au néon sur le toit : pieds, panneau, bordure, tasse fumante.
  const sx = CX - 14;
  const sy = roofTop + 1;
  art.rect(sx + 4, sy + 13, 2, 8, 0x6c7aa3);
  art.rect(sx + 22, sy + 13, 2, 8, 0x6c7aa3);
  art.rect(sx, sy, 28, 14, 0x1b1030);
  for (const [x0, y0, x1, y1] of [
    [sx, sy, sx + 27, sy],
    [sx, sy + 13, sx + 27, sy + 13],
    [sx, sy, sx, sy + 13],
    [sx + 27, sy, sx + 27, sy + 13],
  ] as const) {
    art.line(x0, y0, x1, y1, accent.base);
    lights.line(x0, y0, x1, y1, 0xffb3d9);
  }
  const cupX = CX - 5;
  const cupY = sy + 5;
  art.rect(cupX, cupY, 8, 5, accent.light);
  art.rect(cupX + 8, cupY + 1, 2, 3, accent.light);
  art.hline(cupX - 1, cupX + 9, cupY + 6, accent.light);
  lights.rect(cupX, cupY, 8, 5, 0xffe0f0);
  for (const [x, y] of [
    [cupX + 2, cupY - 2],
    [cupX + 3, cupY - 3],
    [cupX + 5, cupY - 2],
    [cupX + 6, cupY - 3],
  ] as const) {
    art.set(x, y, 0xffe0f0);
    lights.set(x, y, 0xffe0f0);
  }
}

function repairWorkshop(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("repair_workshop");
  const roofTop = hull(art, 14);
  // Porte d'atelier à bandes de danger sur la face droite.
  faceBand(art, roofTop, 34, 55, 3, 9, 0x101a36);
  faceBand(art, roofTop, 34, 55, 3, 9, (i, row) => (row % 3 === 0 ? 0x1a2750 : null));
  faceBand(art, roofTop, 34, 55, 11, 2, (i, row) => ((i + row) % 4 < 2 ? 0xffd166 : 0x101a36));
  faceWindow(art, lights, roofTop, 12, 4, 5, 3, 0xdfe9ff);
  // Grue : mât à l'arrière, flèche en treillis, contrepoids, câble et crochet.
  const mx = CX - 16;
  const jibY = roofTop - 24;
  art.rect(mx, jibY, 4, roofTop + 14 - jibY, accent.dark);
  art.rect(mx, jibY, 2, roofTop + 14 - jibY, accent.base);
  art.hline(mx - 6, mx + 34, jibY, accent.light);
  art.hline(mx - 6, mx + 34, jibY + 4, accent.dark);
  for (let x = mx; x < mx + 34; x += 5) art.line(x, jibY + 4, x + 5, jibY, accent.base);
  art.rect(mx - 9, jibY - 1, 6, 7, 0x5e6a92);
  art.vline(mx + 30, jibY + 5, jibY + 22, 0xa9b6d8);
  art.rect(mx + 28, jibY + 23, 5, 2, 0xdfe7ff);
  art.set(mx + 32, jibY + 25, 0xdfe7ff);
  art.set(mx + 31, jibY + 26, 0xdfe7ff);
  art.rect(mx + 1, jibY - 3, 2, 2, 0xffffff);
  lights.rect(mx, jibY - 4, 4, 4, 0xa9d2ff);
}

function fuelDepot(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("fuel_depot");
  const roofTop = hull(art, 6);
  const tank = tones(accent.base);
  // Réservoir du fond, puis celui de devant ; conduite entre les deux.
  art.cylinder(CX + 10, roofTop + 15, 12, 22, tank, SQUASH);
  cylinderBand(art, CX + 10, roofTop + 15, 12, 6, 2, accent.dark);
  cylinderBand(art, CX + 10, roofTop + 15, 12, 15, 2, accent.dark);
  art.rect(CX - 2, roofTop + 12, 6, 3, 0x8796be);
  art.hline(CX - 2, CX + 3, roofTop + 12, 0xc9d5f0);
  art.cylinder(CX - 11, roofTop + 25, 12, 16, tank, SQUASH);
  cylinderBand(art, CX - 11, roofTop + 25, 12, 5, 2, accent.dark);
  cylinderBand(art, CX - 11, roofTop + 25, 12, 11, 2, accent.dark);
  // Pictogramme de goutte et feu de sécurité.
  art.set(CX - 11, roofTop + 15, accent.deep);
  art.rect(CX - 12, roofTop + 16, 3, 3, accent.deep);
  art.rect(CX + 9, roofTop - 11, 2, 2, 0xfff0d6);
  lights.rect(CX + 8, roofTop - 12, 4, 4, 0xffc387);
}

function warehouse(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("warehouse");
  const roofTop = hull(art, 10);
  // Grande porte coulissante sur la face gauche.
  faceBand(art, roofTop, 6, 24, 1, 8, (i) => (i % 3 === 0 ? 0x101a36 : 0x18264a));
  faceBand(art, roofTop, 6, 24, 1, 1, 0x5a72ad);
  // Caisses empilées sur le toit : planches et sangles.
  const crate: BoxFaces = { top: accent.light, left: accent.base, right: accent.dark, rim: shade(accent.light, 0.3) };
  const drawCrate = (left: number, groundTop: number, height: number) => {
    const top = art.isoBox(left, groundTop, 24, 16, height, crate);
    art.diamondEdge(left + 3, top + 2, 18, 12, accent.dark);
    for (let i = 0; i < 24; i += 6) {
      const faceStart = top + PixelCanvas.diamondBottomRow(24, 16, i) + 1;
      art.vline(left + i, faceStart, faceStart + height - 1, accent.deep);
    }
  };
  drawCrate(CX - 12, roofTop + 4, 12);
  drawCrate(CX - 24, roofTop + 13, 10);
  drawCrate(CX, roofTop + 15, 10);
  lights.rect(FOOT_LEFT + 2, roofTop + 18, 2, 2, 0x9fffa8);
}

function maintenanceBay(art: PixelCanvas, lights: PixelCanvas): void {
  const accent = accentOf("maintenance_bay");
  const roofTop = hull(art, 16);
  // Hangar ouvert sur la face gauche : cadre turquoise, intérieur éclairé, bande de danger au seuil.
  faceBand(art, roofTop, 4, 26, 2, 12, (i, row) => (i === 4 || i === 25 || row === 0 ? accent.base : 0x070b18));
  faceBand(art, roofTop, 6, 24, 6, 6, (i, row) => ((i + row) % 5 === 0 ? 0x123a3c : null));
  faceBand(lights, roofTop, 5, 25, 4, 8, 0x3fd0c9);
  faceBand(art, roofTop, 5, 25, 12, 2, (i, row) => ((i + row) % 4 < 2 ? 0xffd166 : 0x101a36));
  faceWindow(art, lights, roofTop, 40, 4, 8, 2, 0x9ff7f2);
  // Engrenage sur le toit.
  const cx = CX;
  const cy = roofTop + FOOT_H / 2;
  for (const [dx, dy] of [
    [0, -8],
    [0, 7],
    [-12, 0],
    [11, 0],
    [-8, -5],
    [8, -5],
    [-8, 5],
    [8, 5],
  ] as const) {
    art.rect(cx + dx - 1, cy + dy - 1, 3, 2, accent.base);
  }
  art.ellipse(cx, cy, 10, 7, accent.base);
  art.ellipse(cx, cy, 10, 7, accent.base);
  art.ellipse(cx, cy - 1, 8, 5, accent.light);
  art.ellipse(cx, cy, 4, 3, 0x070b18);
  // Antenne et balise.
  art.rect(FOOT_LEFT + FOOT_W - 12, roofTop - 6, 2, 16, 0xa9b6d8);
  art.rect(FOOT_LEFT + FOOT_W - 12, roofTop - 8, 2, 2, 0xffffff);
  lights.rect(FOOT_LEFT + FOOT_W - 13, roofTop - 9, 4, 4, 0x9ff7f2);
}

const DRAW: Record<ModuleType, (body: PixelCanvas, lights: PixelCanvas) => void> = {
  core,
  dock,
  solar_panel: (body) => solarPanel(body),
  battery,
  o2_recycler: o2Recycler,
  habitat,
  cantina,
  repair_workshop: repairWorkshop,
  fuel_depot: fuelDepot,
  warehouse,
  maintenance_bay: maintenanceBay,
};

function isEmpty(canvas: PixelCanvas): boolean {
  for (let i = 3; i < canvas.data.length; i += 4) if ((canvas.data[i] ?? 0) > 0) return false;
  return true;
}

/** Hauteur de la coque de chaque module (celle que son dessin passe à `hull`) : elle place son toit. */
const HULL_HEIGHT: Record<ModuleType, number> = {
  core: 14,
  dock: 6,
  solar_panel: 6,
  battery: 18,
  o2_recycler: 10,
  habitat: 14,
  cantina: 16,
  repair_workshop: 14,
  fuel_depot: 6,
  warehouse: 10,
  maintenance_bay: 16,
};

/** Mât et parabole des modules améliorés : le métal clair de l'antenne du noyau. */
const MAST = { light: 0xc9d5f0, dark: 0x8796be, dish: 0xdfe7ff };
/** Finition du dernier niveau : l'or du soleil. */
const GOLD = { base: 0xffd166, light: 0xfff0b8, dark: shade(0xffd166, -0.4) };

/**
 * Ce qui change sur un module amélioré, cumulé niveau après niveau, sans toucher à sa silhouette : au 2,
 * un liseré d'accent sur l'arête avant du toit ; au 3, une bande d'accent sous le toit ; au 4, un mât
 * d'antenne au coin gauche du toit ; au 5, une parabole au coin droit ; au 6, un feu au sommet du mât et des
 * hublots allumés la nuit dans la bande ; au 7, des balises aux pointes du pont ; au 8, la finition dorée.
 */
function upgradeDecor(art: PixelCanvas, lights: PixelCanvas, type: ModuleType, level: number): void {
  if (level <= 1 || type === "core") return;
  const trim = level >= 8 ? GOLD : accentOf(type);
  const glow = mix(trim.light, 0xffffff, 0.5);
  const roofTop = GROUND_TOP - HULL_HEIGHT[type];
  const roof = PixelCanvas.diamondCorners(FOOT_LEFT, roofTop, FOOT_W, FOOT_H);

  // Liseré : les deux arêtes avant du toit.
  for (let row = Math.floor(FOOT_H / 2); row < FOOT_H; row++) {
    const half = PixelCanvas.diamondHalf(FOOT_W, FOOT_H, row);
    art.set(CX - half, roofTop + row, trim.light);
    art.set(CX + half - 1, roofTop + row, trim.light);
  }
  if (level >= 3) faceBand(art, roofTop, 1, FOOT_W - 1, 1, 1, (i) => (i < FOOT_W / 2 ? trim.base : trim.dark));

  const [lx, ly] = roof.left;
  const mastX = lx + 5;
  const mastBase = ly - 1;
  if (level >= 4) {
    art.rect(mastX - 1, mastBase - 16, 2, 16, MAST.light);
    art.vline(mastX, mastBase - 16, mastBase - 1, MAST.dark);
    art.hline(mastX - 3, mastX + 2, mastBase - 11, MAST.dark);
    art.hline(mastX - 2, mastX + 1, mastBase - 6, MAST.dark);
  }
  if (level >= 5) {
    const [rx, ry] = roof.right;
    const dishX = rx - 6;
    art.vline(dishX, ry - 6, ry - 1, MAST.dark);
    art.ellipse(dishX, ry - 8, 5, 2, MAST.dish);
    art.hline(dishX - 4, dishX + 4, ry - 7, MAST.dark);
    art.rect(dishX - 1, ry - 11, 2, 2, trim.light);
  }
  if (level >= 6) {
    art.rect(mastX - 1, mastBase - 19, 2, 2, trim.light);
    lights.rect(mastX - 2, mastBase - 20, 4, 4, glow);
    lights.rect(mastX - 1, mastBase - 19, 2, 2, 0xffffff);
    for (const i of [9, 17, 41, 49]) faceBand(lights, roofTop, i, i + 3, 1, 1, glow);
  }
  if (level >= 7) {
    const deck = PixelCanvas.diamondCorners(FOOT_LEFT, GROUND_TOP, FOOT_W, FOOT_H);
    for (const [x, y] of [deck.left, deck.right, deck.bottom]) {
      art.rect(x - 1, y - 2, 2, 2, trim.light);
      lights.rect(x - 2, y - 3, 4, 4, glow);
      lights.rect(x - 1, y - 2, 2, 2, 0xffffff);
    }
  }
  if (level >= 8) {
    // Fanion doré au sommet du mât.
    art.rect(mastX + 1, mastBase - 18, 6, 3, GOLD.base);
    art.hline(mastX + 1, mastX + 6, mastBase - 18, GOLD.light);
    art.set(mastX + 6, mastBase - 16, GOLD.dark);
  }
}

/** Sprite d'un module à son niveau d'amélioration (1 : tel que construit). */
export function moduleArt(type: ModuleType, level = 1): ModuleArt {
  const body = new PixelCanvas(MODULE_W, MODULE_H);
  const lights = new PixelCanvas(MODULE_W, MODULE_H);
  DRAW[type](body, lights);
  upgradeDecor(body, lights, type, level);
  body.outline(INK);
  return { body, lights: isEmpty(lights) ? null : lights };
}

/** Échafaudage d'un chantier : arêtes doublées et croisillons d'un bloc, sans remplissage. */
export function scaffoldArt(): PixelCanvas {
  const art = new PixelCanvas(MODULE_W, MODULE_H);
  const color = COLORS.accent;
  const dark = shade(color, -0.35);
  const height = 22;
  const roofTop = GROUND_TOP - height;
  const roof = PixelCanvas.diamondCorners(FOOT_LEFT, roofTop, FOOT_W, FOOT_H);
  const ground = PixelCanvas.diamondCorners(FOOT_LEFT, GROUND_TOP, FOOT_W, FOOT_H);
  const segment = (a: [number, number], b: [number, number], c: Color) => {
    art.line(a[0], a[1], b[0], b[1], c);
    art.line(a[0], a[1] + 1, b[0], b[1] + 1, shade(c, -0.25));
  };
  segment(roof.left, roof.top, color);
  segment(roof.top, roof.right, color);
  segment(roof.right, roof.bottom, color);
  segment(roof.bottom, roof.left, color);
  segment(ground.left, ground.bottom, color);
  segment(ground.bottom, ground.right, color);
  for (const [top, bottom] of [
    [roof.left, ground.left],
    [roof.bottom, ground.bottom],
    [roof.right, ground.right],
  ] as const) {
    art.line(top[0], top[1], bottom[0], bottom[1], color);
    art.line(top[0] + 1, top[1], bottom[0] + 1, bottom[1], dark);
  }
  art.line(roof.left[0], roof.left[1], ground.bottom[0], ground.bottom[1], dark);
  art.line(roof.bottom[0], roof.bottom[1], ground.right[0], ground.right[1], dark);
  art.line(roof.bottom[0], roof.bottom[1], ground.left[0], ground.left[1], dark);
  art.line(roof.right[0], roof.right[1], ground.bottom[0], ground.bottom[1], dark);
  return art;
}

export type ZoneEdgeKind = "open" | "gravity";

/**
 * Limite d'une zone, sans sol : le losange qui entoure les anneaux 1 à `ring` (noyau au centre, une case
 * fait 72 × 48). Trait plein pour la zone constructible (les anneaux ouverts), pointillés violets là où la
 * gravité commence.
 */
export function zoneEdgeArt(kind: ZoneEdgeKind, ring: number): PixelCanvas {
  const w = (2 * ring + 1) * TILE_W;
  const h = (2 * ring + 1) * TILE_H;
  const art = new PixelCanvas(w, h);
  const color = kind === "open" ? COLORS.muted : shade(COLORS.gravity, 0.35);
  const cx = w / 2;
  for (let row = 0; row < h; row++) {
    // Pointillés : six lignes tracées, quatre sautées.
    if (kind === "gravity" && row % 10 >= 6) continue;
    const half = PixelCanvas.diamondHalf(w, h, row);
    for (const x of [cx - half, cx - half + 1, cx + half - 2, cx + half - 1]) art.set(x, row, color);
    // La zone ouverte a un liseré intérieur, plus discret.
    if (kind === "open") for (const x of [cx - half + 2, cx + half - 3]) art.set(x, row, color, 90);
  }
  return art;
}

export type HighlightKind = "build" | "blocked" | "cursor" | "select";

/** Marques posées sur une case : constructible, interdite, curseur clavier, module choisi. */
export function highlightArt(kind: HighlightKind): PixelCanvas {
  const art = new PixelCanvas(TILE_W, TILE_H);
  if (kind === "build" || kind === "blocked") {
    const color = kind === "build" ? COLORS.accent : COLORS.stop;
    art.diamond(0, 0, TILE_W, TILE_H, color, color, 64);
    art.diamondEdge(0, 0, TILE_W, TILE_H, color);
    art.diamondEdge(4, 3, TILE_W - 8, TILE_H - 6, shade(color, -0.3));
    if (kind === "blocked") {
      art.line(TILE_W / 2 - 9, TILE_H / 2 - 6, TILE_W / 2 + 8, TILE_H / 2 + 5, color);
      art.line(TILE_W / 2 + 8, TILE_H / 2 - 6, TILE_W / 2 - 9, TILE_H / 2 + 5, color);
      art.line(TILE_W / 2 - 8, TILE_H / 2 - 6, TILE_W / 2 + 9, TILE_H / 2 + 5, color);
      art.line(TILE_W / 2 + 9, TILE_H / 2 - 6, TILE_W / 2 - 8, TILE_H / 2 + 5, color);
    }
    return art;
  }
  // Coins de visée sur les quatre pointes du losange, tracés le long des arêtes.
  const color = kind === "cursor" ? COLORS.accent : COLORS.ink;
  const c = PixelCanvas.diamondCorners(0, 0, TILE_W, TILE_H);
  const toward = (from: [number, number], to: [number, number], t: number): [number, number] => [
    Math.round(from[0] + (to[0] - from[0]) * t),
    Math.round(from[1] + (to[1] - from[1]) * t),
  ];
  const pairs: [[number, number], [number, number]][] = [
    [c.top, c.left],
    [c.top, c.right],
    [c.bottom, c.left],
    [c.bottom, c.right],
    [c.left, c.top],
    [c.left, c.bottom],
    [c.right, c.top],
    [c.right, c.bottom],
  ];
  for (const [from, to] of pairs) {
    const [x, y] = toward(from, to, 0.3);
    art.line(from[0], from[1], x, y, color);
    art.line(from[0], from[1] + (from[1] < TILE_H / 2 ? 1 : -1), x, y + (from[1] < TILE_H / 2 ? 1 : -1), color);
  }
  return art;
}

export type SignKind = "stage1" | "stage2" | "stage3" | "job" | "off" | "unpowered" | "unstaffed";

/** Panneaux au-dessus des modules : panne (par palier), intervention, veille, énergie, équipage. */
export function signArt(kind: SignKind): PixelCanvas {
  const art = new PixelCanvas(22, 22);
  if (kind.startsWith("stage")) {
    const color = kind === "stage1" ? COLORS.warn : kind === "stage2" ? COLORS.alert : COLORS.stop;
    for (let row = 0; row < 18; row++) {
      const half = Math.floor(row / 2) + 1;
      art.hline(11 - half, 10 + half, row + 2, row < 3 ? shade(color, 0.3) : color);
    }
    art.hline(2, 19, 19, shade(color, -0.3));
    art.rect(10, 7, 2, 7, INK);
    art.rect(10, 15, 2, 2, INK);
    art.outline(INK);
    return art;
  }
  const badge = kind === "job" ? COLORS.good : COLORS.panel2;
  art.ellipse(11, 11, 10, 10, shade(badge, -0.3));
  art.ellipse(11, 10, 9, 9, badge);
  const glyph = kind === "job" ? INK : COLORS.muted;
  if (kind === "job") {
    // Clé plate.
    art.line(6, 15, 14, 7, glyph);
    art.line(7, 15, 15, 7, glyph);
    art.rect(14, 4, 2, 3, glyph);
    art.rect(16, 6, 2, 2, glyph);
    art.rect(4, 15, 3, 2, glyph);
  } else if (kind === "unpowered") {
    for (const [x, y] of [
      [12, 4],
      [11, 5],
      [10, 6],
      [9, 7],
      [8, 8],
      [9, 9],
      [10, 9],
      [11, 9],
      [12, 9],
      [11, 10],
      [10, 11],
      [9, 12],
      [8, 13],
    ] as const) {
      art.rect(x, y, 2, 1, COLORS.warn);
    }
  } else if (kind === "unstaffed") {
    art.rect(9, 4, 4, 4, glyph);
    art.rect(7, 9, 8, 6, glyph);
    art.rect(8, 15, 2, 2, glyph);
    art.rect(12, 15, 2, 2, glyph);
  } else {
    // Veille : le symbole marche-arrêt.
    art.rect(10, 4, 2, 7, glyph);
    art.line(6, 7, 5, 11, glyph);
    art.line(15, 7, 16, 11, glyph);
    art.line(5, 12, 8, 15, glyph);
    art.line(16, 12, 13, 15, glyph);
    art.hline(8, 13, 16, glyph);
  }
  art.outline(INK);
  return art;
}

const SHIP_ACCENTS: Record<ShipClass, Color> = { shuttle: 0x6fd3ff, freighter: 0xffd166, liner: 0xb49cff };

export interface ShipArt {
  body: PixelCanvas;
  lights: PixelCanvas;
  /** Tuyère, d'où sort la flamme (coordonnées du sprite, tourné vers la droite). */
  nozzle: { x: number; y: number };
}

/** Vaisseaux vus de trois quarts, tournés vers la droite (retournés quand ils vont à gauche). */
export function shipArt(shipClass: ShipClass): ShipArt {
  const accent = SHIP_ACCENTS[shipClass];
  const hull = 0xdfe7ff;
  const lit = 0xffffff;
  const shadow = 0x9aa8cc;
  const dark = 0x5e6a92;
  if (shipClass === "shuttle") {
    const body = new PixelCanvas(38, 22);
    const lights = new PixelCanvas(38, 22);
    body.rect(6, 6, 20, 8, hull);
    body.rect(6, 6, 20, 2, lit);
    body.rect(7, 14, 18, 2, shadow);
    body.rect(26, 7, 4, 7, hull);
    body.rect(30, 9, 2, 4, hull);
    body.set(32, 10, hull);
    // Verrière et bande de couleur.
    body.rect(22, 7, 6, 3, accent);
    body.rect(22, 7, 2, 1, 0xffffff);
    lights.rect(22, 7, 6, 3, 0xdff8ff);
    body.hline(6, 21, 11, shade(accent, -0.15));
    // Aile et dérive.
    body.rect(10, 16, 10, 2, dark);
    body.rect(6, 3, 4, 3, shadow);
    // Réacteur.
    body.rect(3, 8, 3, 5, dark);
    body.outline(INK);
    return { body, lights, nozzle: { x: 3, y: 10 } };
  }
  if (shipClass === "freighter") {
    const body = new PixelCanvas(54, 28);
    const lights = new PixelCanvas(54, 28);
    // Châssis, poste de pilotage à l'avant.
    body.rect(5, 13, 38, 6, shadow);
    body.rect(5, 13, 38, 1, hull);
    body.rect(36, 6, 9, 13, hull);
    body.rect(36, 6, 9, 2, lit);
    body.rect(45, 10, 3, 7, hull);
    body.set(48, 13, hull);
    body.rect(39, 8, 4, 4, accent);
    lights.rect(39, 8, 4, 4, 0xfff3c2);
    // Conteneurs.
    for (const x of [7, 17, 27]) {
      body.rect(x, 4, 8, 9, accent);
      body.rect(x, 4, 8, 2, shade(accent, 0.45));
      body.vline(x + 7, 5, 12, shade(accent, -0.4));
      body.vline(x + 3, 6, 12, shade(accent, -0.2));
    }
    body.rect(6, 19, 34, 2, dark);
    body.rect(2, 13, 3, 6, dark);
    body.outline(INK);
    return { body, lights, nozzle: { x: 2, y: 16 } };
  }
  const body = new PixelCanvas(62, 26);
  const lights = new PixelCanvas(62, 26);
  body.rect(6, 8, 44, 8, hull);
  body.rect(6, 8, 44, 2, lit);
  body.rect(50, 10, 4, 6, hull);
  body.rect(54, 12, 2, 3, hull);
  body.set(56, 13, hull);
  body.rect(6, 16, 42, 2, shadow);
  body.hline(6, 49, 14, shade(accent, -0.1));
  body.hline(6, 49, 15, accent);
  // Rangée de hublots, allumés la nuit.
  for (let x = 11; x < 47; x += 4) {
    body.rect(x, 11, 2, 2, 0x0b1430);
    lights.rect(x, 11, 2, 2, 0xfff0c8);
  }
  body.rect(16, 4, 16, 4, shade(accent, 0.2));
  body.rect(16, 4, 16, 1, shade(accent, 0.5));
  body.rect(16, 18, 16, 2, dark);
  body.rect(2, 9, 4, 6, dark);
  body.outline(INK);
  return { body, lights, nozzle: { x: 2, y: 12 } };
}

/** Coins de sélection d'un vaisseau : quatre équerres autour de lui. */
export function bracketArt(width: number, height: number): PixelCanvas {
  const art = new PixelCanvas(width, height);
  const arm = 7;
  for (const [x, y, sx, sy] of [
    [0, 0, 1, 1],
    [width - 1, 0, -1, 1],
    [0, height - 1, 1, -1],
    [width - 1, height - 1, -1, -1],
  ] as const) {
    for (let k = 0; k < arm; k++) {
      art.set(x + sx * k, y, 0xffffff);
      art.set(x + sx * k, y + sy, 0xffffff);
      art.set(x, y + sy * k, 0xffffff);
      art.set(x + sx, y + sy * k, 0xffffff);
    }
  }
  return art;
}

/** Flamme de tuyère, en deux images qui alternent. */
export function flameArt(frame: 0 | 1): PixelCanvas {
  const art = new PixelCanvas(12, 6);
  const length = frame === 0 ? 11 : 8;
  art.rect(12 - length, 1, length, 4, 0xff7a3c);
  art.rect(12 - length + 2, 2, length - 2, 2, 0xffb347);
  art.rect(9, 2, 3, 2, 0xfff3c2);
  return art;
}

/**
 * Besoin d'un vaisseau, au-dessus de lui sur le plan : son pictogramme (le même que dans l'interface) sur
 * une plaque sombre, lisible sur le pont comme sur le ciel.
 */
export function needDotArt(kind: NeedKind): PixelCanvas {
  const art = new PixelCanvas(14, 14);
  art.rect(1, 1, 12, 12, COLORS.deep);
  for (const [x, y] of [
    [1, 1],
    [12, 1],
    [1, 12],
    [12, 12],
  ] as const) {
    art.set(x, y, 0, 0);
  }
  const color = needColor(kind);
  NEED_ICONS[kind].forEach((row, y) => {
    for (let x = 0; x < row.length; x++) if (row[x] === "#") art.set(2 + x, 2 + y, color);
  });
  art.outline(INK);
  return art;
}

export const NEED_DOT_KINDS = NEED_KINDS;

export type WearArtLevel = "worn" | "critical";
/** Segments de la jauge d'usure du plan. */
export const WEAR_SEGMENTS = 6;

/**
 * Usure d'un module sur le plan, posée sur le module lui-même : une plaquette cerclée de la couleur de
 * l'usure (ambre, puis orange), des segments remplis d'un tramage d'autant plus dense que le module est
 * fatigué, comme dans sa fiche. Sans pictogramme : rien ne la confond avec les besoins d'un vaisseau.
 */
export function wearArt(level: WearArtLevel, filled: number): PixelCanvas {
  const segmentW = 3;
  const inner = WEAR_SEGMENTS * (segmentW + 1) - 1;
  const width = inner + 4;
  const height = 8;
  const color = level === "critical" ? COLORS.alert : COLORS.warn;
  const art = new PixelCanvas(width, height);
  // Cadre de la couleur de l'usure, coins coupés, fond sombre.
  art.rect(0, 0, width, height, color);
  art.rect(1, 1, width - 2, height - 2, COLORS.deep);
  for (const [x, y] of [
    [0, 0],
    [width - 1, 0],
    [0, height - 1],
    [width - 1, height - 1],
  ] as const) {
    art.set(x, y, 0, 0);
  }
  // Usé : un pixel sur deux ; critique : plein.
  const lit = (x: number, y: number) => level === "critical" || (x + y) % 2 === 0;
  for (let i = 0; i < WEAR_SEGMENTS; i++) {
    const left = 2 + i * (segmentW + 1);
    for (let y = 2; y < height - 2; y++) {
      for (let x = left; x < left + segmentW; x++) art.set(x, y, i < filled && lit(x, y) ? color : 0x18213c);
    }
  }
  const framed = new PixelCanvas(width + 2, height + 2);
  framed.stamp(art, 1, 1);
  framed.outline(INK);
  return framed;
}

/** Volute de fumée (pannes aggravées). */
export function puffArt(): PixelCanvas {
  const art = new PixelCanvas(14, 12);
  art.ellipse(7, 7, 6, 4, 0x6c7591);
  art.ellipse(6, 5, 4, 3, 0x8a93ad);
  art.rect(4, 3, 3, 2, 0xb8c0d6);
  return art;
}

/** Un pixel blanc, teinté et étiré pour les jauges. */
export function pixelArt(): PixelCanvas {
  const art = new PixelCanvas(1, 1);
  art.set(0, 0, 0xffffff);
  return art;
}

/** Ciel étoilé, répété en mosaïque. */
export function starsArt(size = 256): PixelCanvas {
  const art = new PixelCanvas(size, size);
  const rng = createRng("stars");
  for (let i = 0; i < 220; i++) {
    const x = Math.floor(rng.next() * size);
    const y = Math.floor(rng.next() * size);
    const roll = rng.next();
    if (roll < 0.05) {
      art.rect(x, y, 2, 2, 0xffffff);
      for (const [dx, dy] of [
        [2, 0],
        [-1, 0],
        [0, 2],
        [0, -1],
        [1, 2],
        [2, 1],
      ] as const) {
        art.set(x + dx, y + dy, 0x6f86c4);
      }
    } else {
      art.set(x, y, roll < 0.25 ? 0xdfe7ff : roll < 0.5 ? 0x8f9cff : roll < 0.55 ? 0xffd9a8 : 0x4a5a8a);
    }
  }
  return art;
}

/** Soleil : un disque blanc chaud, sa couronne et ses rayons, en pixels. */
export function sunArt(): PixelCanvas {
  const art = new PixelCanvas(72, 72);
  const c = 36;
  // Halo tramé, puis couronne, puis disque.
  for (let y = 0; y < 72; y++) {
    for (let x = 0; x < 72; x++) {
      const d = Math.hypot(x + 0.5 - c, y + 0.5 - c);
      if (d < 34 && d >= 22 && (x + y) % 2 === 0) art.set(x, y, 0xffd166, Math.round(150 * (1 - (d - 22) / 12)));
      else if (d < 22 && d >= 16) art.set(x, y, 0xffd166, 210);
    }
  }
  art.ellipse(c, c, 16, 16, 0xffd98a);
  art.ellipse(c, c, 13, 13, 0xfff3c2);
  art.ellipse(c - 3, c - 3, 7, 7, 0xffffff);
  for (let k = 0; k < 8; k++) {
    const angle = (k * Math.PI) / 4;
    const x0 = Math.round(c + Math.cos(angle) * 19);
    const y0 = Math.round(c + Math.sin(angle) * 19);
    const x1 = Math.round(c + Math.cos(angle) * 27);
    const y1 = Math.round(c + Math.sin(angle) * 27);
    art.line(x0, y0, x1, y1, 0xffe8a8);
  }
  return art;
}

export type SkyKind = "day" | "night";

/** Ciel en bandes de dégradé (étiré sur tout l'écran) : bleu profond le jour, nuit noire dans l'ombre. */
export function skyArt(kind: SkyKind): PixelCanvas {
  const height = 64;
  const art = new PixelCanvas(1, height);
  const stops: [number, Color][] =
    kind === "day"
      ? [
          [0, 0x081634],
          [0.55, 0x10285a],
          [0.85, 0x1d3f7a],
          [1, 0x2f5f9c],
        ]
      : [
          [0, 0x02030a],
          [0.7, 0x040814],
          [1, 0x081028],
        ];
  for (let y = 0; y < height; y++) {
    const t = y / (height - 1);
    let i = 0;
    while (i < stops.length - 2 && t > (stops[i + 1] as [number, Color])[0]) i++;
    const [t0, c0] = stops[i] as [number, Color];
    const [t1, c1] = stops[i + 1] as [number, Color];
    const local = (t - t0) / Math.max(0.0001, t1 - t0);
    art.set(0, y, mix(c0, c1, local));
  }
  return art;
}

export type PlanetKind = "day" | "night";

/**
 * La planète, au ras du bas de l'écran. Le jour : océans et nuages éclairés, limbe lumineux. La nuit (la
 * station est dans son ombre) : une face sombre semée de villes allumées.
 */
export function planetArt(kind: PlanetKind, width = 640, height = 144): PixelCanvas {
  const art = new PixelCanvas(width, height);
  const radius = width * 0.95;
  const cx = width / 2;
  const cy = radius + 18;
  const rng = createRng("planet");
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
      if (d > radius + 6) continue;
      if (d > radius) {
        // Atmosphère tramée au-dessus du limbe.
        const glow = kind === "day" ? 0x6fd3ff : 0x1c4a7a;
        if ((x + y) % 2 === 0) art.set(x, y, glow, Math.round(200 * (1 - (d - radius) / 6)));
        continue;
      }
      const depth = radius - d;
      if (depth < 2) {
        art.set(x, y, kind === "day" ? 0xbff0ff : 0x2a5f9a);
        continue;
      }
      let color: Color;
      if (kind === "day") {
        color = depth < 8 ? 0x2a6fb8 : depth < 20 ? 0x1a4f94 : 0x123c7a;
        if (depth >= 8 && depth < 20 && (x + y) % 2 === 0) color = 0x1f5aa3;
        const band = Math.sin(x / 31 + Math.sin(y / 9)) * 4 + y * 0.9;
        if (depth > 22 && band % 23 < 3.2) color = 0x8fb8e8;
        if (depth > 26 && Math.sin(x / 47) + Math.cos(y / 13 + x / 90) > 1.25) color = 0x2b6f6a;
      } else {
        color = depth < 8 ? 0x0c1f44 : 0x060d1f;
        if (depth >= 8 && depth < 16 && (x + y) % 2 === 0) color = 0x0a1832;
      }
      art.set(x, y, color);
    }
  }
  if (kind === "night") {
    // Villes : des grappes de lumières sur la face nocturne (autant par largeur, quelle que soit sa taille).
    for (let k = 0; k < Math.round(width * 0.14); k++) {
      const x = Math.floor(rng.next() * width);
      const y = Math.floor(30 + rng.next() * (height - 30));
      const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
      if (radius - d < 14) continue;
      const size = rng.next() < 0.3 ? 3 : 1;
      for (let i = 0; i < size * 2; i++) {
        const lx = x + Math.floor(rng.next() * size * 2) - size;
        const ly = y + Math.floor(rng.next() * size) - Math.floor(size / 2);
        art.set(lx, ly, rng.next() < 0.7 ? 0xffd98a : 0xffb347);
      }
    }
  }
  return art;
}

/** Police pixel des gains (« +12 cr ») : glyphes de 5 × 7, contour compris dans 8 × 10. */
export const FONT_CHARS = "0123456789+-cr";
const GLYPHS: Record<string, string[]> = {
  "0": [".###.", "#...#", "#..##", "#.#.#", "##..#", "#...#", ".###."],
  "1": ["..#..", ".##..", "..#..", "..#..", "..#..", "..#..", ".###."],
  "2": [".###.", "#...#", "....#", "...#.", "..#..", ".#...", "#####"],
  "3": ["####.", "....#", "....#", ".###.", "....#", "....#", "####."],
  "4": ["...#.", "..##.", ".#.#.", "#..#.", "#####", "...#.", "...#."],
  "5": ["#####", "#....", "####.", "....#", "....#", "#...#", ".###."],
  "6": [".###.", "#....", "#....", "####.", "#...#", "#...#", ".###."],
  "7": ["#####", "....#", "...#.", "..#..", ".#...", ".#...", ".#..."],
  "8": [".###.", "#...#", "#...#", ".###.", "#...#", "#...#", ".###."],
  "9": [".###.", "#...#", "#...#", ".####", "....#", "....#", ".###."],
  "+": [".....", "..#..", "..#..", "#####", "..#..", "..#..", "....."],
  "-": [".....", ".....", ".....", "#####", ".....", ".....", "....."],
  c: [".....", ".....", ".####", "#....", "#....", "#....", ".####"],
  r: [".....", ".....", "#.###", "##...", "#....", "#....", "#...."],
};
export const GLYPH_W = 8;
export const GLYPH_H = 10;
/** Avance d'un glyphe dans un texte (le contour se chevauche d'un pixel). */
export const GLYPH_ADVANCE = 7;

export function fontArt(color: Color): PixelCanvas {
  const art = new PixelCanvas(GLYPH_W * FONT_CHARS.length, GLYPH_H);
  [...FONT_CHARS].forEach((char, index) => {
    const rows = GLYPHS[char] ?? [];
    rows.forEach((row, y) => {
      [...row].forEach((cell, x) => {
        if (cell === "#") art.set(index * GLYPH_W + 1 + x, 1 + y, y < 2 ? shade(color, 0.35) : color);
      });
    });
  });
  art.outline(INK);
  return art;
}

export const ALL_MODULE_TYPES = MODULE_TYPES;
